import { Table } from 'semantic-ui-react';
import React, { useRef, useState } from 'react';
import { ColumnDefinition } from '../../interfaces/ColumnDefinition';
import { TableSelectedCells } from '../../interfaces/TableSelectedCells';
import clsx from 'clsx';
import { SelectCellsRectangle } from './SelectCellsRectangle/SelectCellsRectangle';

import './TableCell.less';

interface Props<TData> {
  item: TData;
  columnDefinition: ColumnDefinition<TData, any, any>;
  getId: (item: TData) => string;
  rowPosition?: number;
  columnPosition?: number;
  isSelectingCellsActive?: boolean;
  fixedSelectedCells?: TableSelectedCells;
  selectedCells?: TableSelectedCells;
  onSetInitialSelectedCell?: (
    rowPosition: number,
    columnPosition: number
  ) => void;
  onSelectingCellsStarted?: () => void;
  onSelectingCellsFinished?: () => void;
  onHoverCell?: (rowPosition: number, columnPosition: number) => void;
}

const checkIsCellInSelectedRange = (
  rowPosition: number,
  columnPosition: number,
  selectedCells: TableSelectedCells | undefined
) => {
  if (!selectedCells) return false;
  return (
    rowPosition >= selectedCells.rowPosition.from &&
    rowPosition <= selectedCells.rowPosition.to &&
    columnPosition >= selectedCells.columnPosition.from &&
    columnPosition <= selectedCells.columnPosition.to
  );
};

export const TableCell = <TData,>(props: Props<TData>) => {
  const { rowPosition = 0, columnPosition = 0 } = props;
  const [clickedElementOnMouseDown, setClickedElementOnMouseDown] =
    useState<EventTarget | null>();
  const elementSelectCellsRectangle = useRef<HTMLDivElement>(null);
  const isCellSelected = checkIsCellInSelectedRange(
    rowPosition,
    columnPosition,
    props.selectedCells
  );
  const isCellFixed = checkIsCellInSelectedRange(
    rowPosition,
    columnPosition,
    props.fixedSelectedCells
  );
  const isCellSelectedOrFixed = isCellFixed || isCellSelected;
  const isShowSelectCellsRectangle =
    props.columnPosition === props.fixedSelectedCells?.columnPosition.to &&
    props.rowPosition === props.fixedSelectedCells?.rowPosition.to;

  const handleClick = () => {
    if (
      clickedElementOnMouseDown !== elementSelectCellsRectangle.current &&
      !props.columnDefinition.isDisabled
    ) {
      props.onSetInitialSelectedCell?.(rowPosition, columnPosition);
    }
  };

  const handleMouseDown = (e: Event) => setClickedElementOnMouseDown(e.target);

  const getSelectedCellClassesObject = (): Record<string, boolean> => {
    const classes: Record<string, boolean> = {};
    if (props.fixedSelectedCells && isCellFixed) {
      classes.tableCell__selectedCell_fixedBorderTop =
        props.fixedSelectedCells.rowPosition.from === rowPosition;
      classes.tableCell__selectedCell_fixedBorderBottom =
        props.fixedSelectedCells.rowPosition.to === rowPosition;
      classes.tableCell__selectedCell_fixedBorderLeft =
        props.fixedSelectedCells.columnPosition.from === columnPosition;
      classes.tableCell__selectedCell_fixedBorderRight =
        props.fixedSelectedCells.columnPosition.to === columnPosition;
    }
    if (props.selectedCells && isCellSelected) {
      classes.tableCell__selectedCell_selectedBorderTop =
        props.selectedCells.rowPosition.from === rowPosition;
      classes.tableCell__selectedCell_selectedBorderBottom =
        props.selectedCells.rowPosition.to === rowPosition;
      classes.tableCell__selectedCell_selectedBorderLeft =
        props.selectedCells.columnPosition.from === columnPosition;
      classes.tableCell__selectedCell_selectedBorderRight =
        props.selectedCells.columnPosition.to === columnPosition;
    }

    return classes;
  };

  return (
    <Table.Cell
      width={props.columnDefinition.width}
      singleLine={props.columnDefinition.singleLine}
      data-label={props.columnDefinition.name}
      onClick={handleClick}
      onMouseDown={handleMouseDown}
      onMouseEnter={() => props.onHoverCell?.(rowPosition, columnPosition)}
      className={clsx({
        tableCell_selectingCellsStarted: !!props.isSelectingCellsActive,
        tableCell_selectedCell: isCellSelectedOrFixed,
      })}
    >
      {props.columnDefinition.cell
        ? props.columnDefinition.cell(props.item, () => null)
        : props.columnDefinition.selector
        ? props.columnDefinition.selector(props.item)
        : 'NO DATA'}
      {isCellSelectedOrFixed && (
        <div
          className={clsx(
            'tableCell__selectedCell',
            getSelectedCellClassesObject()
          )}
        />
      )}
      {isShowSelectCellsRectangle &&
        props.onSelectingCellsStarted &&
        props.onSelectingCellsFinished && (
          <SelectCellsRectangle
            ref={elementSelectCellsRectangle}
            isSelectingCellsActive={!!props.isSelectingCellsActive}
            onSelectingCellsStarted={props.onSelectingCellsStarted}
            onSelectingCellsFinished={props.onSelectingCellsFinished}
          />
        )}
    </Table.Cell>
  );
};
